import Config from '@config';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import { BaseQueryApi } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';

import { marketGasPricesApi } from '@/features/marketGasPrices';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  PONPriceSettings,
  PONPriceSettingsRequestBody,
  PONPriceSettingsSlim,
} from './types';

const refreshVtpQuotationLookup = async (
  vtpQuotation: number | null,
  {
    dispatch,
    queryFulfilled,
  }: Pick<BaseQueryApi, 'dispatch'> & { queryFulfilled: Promise<unknown> },
) => {
  if (vtpQuotation === null) return;

  const isSaved = await queryFulfilled.then(
    () => true,
    () => false,
  );
  if (isSaved)
    dispatch(marketGasPricesApi.util.invalidateTags(['MarketGasPrices']));
};

export const ponPriceSettingsApi = createApi({
  reducerPath: 'ponPriceSettingsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/provider-of-last-resort-prices`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['PONPriceSettings', 'PONPriceSettingsList'],
  endpoints: builder => ({
    getPONPriceSettingsI: builder.infiniteQuery<
      PONPriceSettingsSlim[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['PONPriceSettings', 'PONPriceSettingsList'],
    }),
    getPONPriceSettingsById: builder.query<PONPriceSettings, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['PONPriceSettings'],
    }),
    createPONPriceSettings: builder.mutation<
      string,
      PONPriceSettingsRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['PONPriceSettingsList', 'PONPriceSettings'],
      onQueryStarted: ({ vtpQuotation }, api) =>
        refreshVtpQuotationLookup(vtpQuotation, api),
    }),
    updatePONPriceSettings: builder.mutation<
      void,
      { id: string; body: PONPriceSettingsRequestBody }
    >({
      query: ({ id, body }) => ({
        url: `/${id}`,
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: ['PONPriceSettingsList', 'PONPriceSettings'],
      onQueryStarted: ({ body }, api) =>
        refreshVtpQuotationLookup(body.vtpQuotation, api),
    }),
  }),
});

export const {
  useGetPONPriceSettingsByIdQuery,
  useCreatePONPriceSettingsMutation,
  useUpdatePONPriceSettingsMutation,
} = ponPriceSettingsApi;

export const useGetPONPriceSettingsIInfiniteQuery =
  normalizeInfiniteQueryData<PONPriceSettingsSlim>(
    ponPriceSettingsApi.useGetPONPriceSettingsIInfiniteQuery,
  );
